import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { EvDialogModule } from 'ev-dialog';
import { EvLogoLoaderComponent } from 'src/app/shared-components/ev-logo-loader/ev-logo-loader.component';
import { MarketSalesStateService } from 'src/services/estate/market-sales-state.service';
import { ReportsService } from 'src/services/reports.service';
import { EvAuthorizationService } from 'src/util/auth0/ev-auth.service';
import { Auth0UserProfileModel } from 'src/util/auth0/models/auth0-user-profile.model';
import { BrowserUtil } from 'src/util/browser/browser.util';
import { SubscriptionSelectedFiltersComponent } from './subscription-selected-filters/subscription-selected-filters.component';
import { catchError, finalize, of, take } from "rxjs";
import { ValidateUtil } from 'src/util/tools/validate.util';
import { environment } from 'src/environments/environment';

const VALIDATION_DEBOUNCE_MS = 400;

@Component({
  selector: 'subscription-report-wizard',
  imports: [EvDialogModule, CommonModule, EvLogoLoaderComponent, FormField, SubscriptionSelectedFiltersComponent],
  templateUrl: './report-wizard.component.html',
  styleUrl: './report-wizard.component.scss',
})
export class ReportWizardComponent {
    showLoader = input<boolean>(false);
    onClose = output<boolean>();
    onConfirm = output<any>();

    subscriptionReportModel = signal<SubscriptionReportModel>({
        reportTitle: "",
        recipients: [{ recipient: "" }],
    });
    subscriptionReportForm = form(this.subscriptionReportModel);

    private readonly authorizationService = inject(EvAuthorizationService);
    private readonly mapStateService = inject(MarketSalesStateService);
    reportsService = inject(ReportsService);

    infoToggleOpen: boolean = false;
    days: string[][] = [
        // days as 1-7, mon-sun
        ["", ""],
        ["man", "Mandag"],
        ["tir", "Tirsdag"],
        ["ons", "Onsdag"],
        ["tors", "Torsdag"],
        ["fre", "Fredag"],
        ["lør", "Lørdag"],
        ["søn", "Søndag"],
    ];
    weekArray: number[] = [1, 2, 3, 4, 5, 6, 7];
    currentDayIdx: number;
    addMoreIsOpen: boolean = false;
    readyToClose: boolean = false;
    triggerSubscriptionSuccess: boolean = false;
    currentUser: Auth0UserProfileModel;
    isMobileView: boolean = false;
    ddOpen: boolean = false;
    processing: boolean = false;
    isDebug: boolean;
    filterFormAPI = this.mapStateService.filterFormAPI;

    currentDayText: string;
    readonly support = environment.support;

    // Non-empty recipients, normalised. Only emits when the list actually changes, so title edits don't trigger validation.
    private readonly recipientEmails = computed(() => this.cleanEmails(this.subscriptionReportModel().recipients.map((x) => x.recipient.trim().toLowerCase())), {
        equal: (a, b) => a.length === b.length && a.every((email, i) => email === b[i]),
    });
    // recipientEmails after the user has stopped typing; errors are shown for this snapshot only.
    private readonly debouncedEmails = signal<string[]>([]);
    // Backend answer per domain: true = approved for the user, false = not approved.
    private readonly domainApproval = signal<Record<string, boolean>>({});
    private readonly pendingDomains = new Set<string>();

    readonly validationErrors = computed<IValidationErrors>(() => {
        const emails = this.debouncedEmails();
        const approval = this.domainApproval();
        const validEmails = emails.filter((email) => ValidateUtil.isValidEmail(email, false));
        const domains = [...new Set(validEmails.map((email) => this.getDomain(email)))];

        return {
            noTitle: this.subscriptionReportModel().reportTitle.trim().length === 0,
            noRecipients: this.recipientEmails().length === 0,
            notValidEmails: emails.filter((email) => !validEmails.includes(email)),
            notValidDomains: domains.filter((domain) => approval[domain] === false),
            pendingValidation: this.debouncedEmails() !== this.recipientEmails() || domains.some((domain) => approval[domain] === undefined),
        };
    });

    readonly hasError = computed(() => {
        const errors = this.validationErrors();
        return errors.noTitle || errors.noRecipients || errors.pendingValidation || errors.notValidEmails.length > 0 || errors.notValidDomains.length > 0;
    });

    // "@a.no", "@a.no og @b.no", "@a.no, @b.no og @c.no"
    readonly notValidDomainsText = computed(() => {
        const domains = this.validationErrors().notValidDomains.map((domain) => "@" + domain);
        return domains.length > 1 ? domains.slice(0, -1).join(", ") + " og " + domains[domains.length - 1] : domains.join("");
    });

    constructor() {
        this.initialise();
        const viewPort = BrowserUtil.getViewport();
        this.isMobileView = viewPort.width <= 640;

        effect((onCleanup) => {
            const emails = this.recipientEmails();
            const timer = setTimeout(() => this.debouncedEmails.set(emails), VALIDATION_DEBOUNCE_MS);
            onCleanup(() => clearTimeout(timer));
        });

        effect(() => {
            const domains = this.debouncedEmails()
                .filter((email) => ValidateUtil.isValidEmail(email, false))
                .map((email) => this.getDomain(email));
            const approval = untracked(this.domainApproval);

            new Set(domains).forEach((domain) => {
                if (approval[domain] === undefined && !this.pendingDomains.has(domain)) {
                    this.validateDomain(domain);
                }
            });
        });
    }

    confirmReport() {
        if (this.readyToClose) {
            this.readyToClose = false;
            this.onClose.emit(false);
        } else if (!this.hasError()) {
            this.createAndSubscribe();
            this.readyToClose = true;
        }
    }

    toggleDayDropDown(): void {
        this.ddOpen = !this.ddOpen;
    }

    selectDay(idx: number): void {
        this.setDay(idx);
        this.ddOpen = false;
    }

    openAddMoreRecipients(): void {
        this.pushEmptyRecipient();
        this.addMoreIsOpen = true;
    }

    pushEmptyRecipient(): void {
        this.subscriptionReportModel.update((model) => ({ ...model, recipients: [...model.recipients, { recipient: "" }] }));
    }

    removeRecipient(idx: number): void {
        this.subscriptionReportModel.update((model) => ({ ...model, recipients: model.recipients.filter((_, i) => i !== idx) }));
    }

    private validateDomain(domain: string): void {
        this.pendingDomains.add(domain);
        this.authorizationService
            .isUserDomain(domain)
            .pipe(
                take(1),
                catchError((e) => {
                    console.error("Domain validation failed", e);
                    return of(false);
                }),
                finalize(() => this.pendingDomains.delete(domain)),
            )
            .subscribe((isApproved) => {
                this.domainApproval.update((approval) => ({ ...approval, [domain]: isApproved === true }));
            });
    }

    private getDomain(email: string): string {
        return email.split("@")[1];
    }

    private cleanEmails(emails: string[]): string[] {
        const filtered = emails.filter((el) => {
            return el != "";
        });
        return [...new Set(filtered)];
    }

    private createAndSubscribe(): void {
        this.processing = true;
        this.reportsService
            .createAndSubscribe(this.recipientEmails(), this.subscriptionReportModel().reportTitle, this.currentDayIdx, this.filterFormAPI())
            .pipe(
                take(1),
                catchError((e) => {
                    console.error("Trigger subscription failed", e);
                    this.triggerSubscriptionSuccess = false;
                    return of(null);
                }),
                finalize(() => {
                    this.resetDialog();
                    this.processing = false;
                }),
            )
            .subscribe((result) => {
                if (result !== null) {
                    this.triggerSubscriptionSuccess = true;
                }
            });
    }

    closeWizard(): void {
        this.resetDialog();
        this.onClose.emit(true);
    }

    setDay(idx: number): void {
        this.currentDayIdx = idx;
        this.currentDayText = this.days[idx][1];
    }

    private initialise(): void {
        this.initDay();
        this.resetRecipients();
    }

    private initDay(): void {
        const d = new Date();
        if (d.getDay() === 0) {
            this.setDay(7);
        } else {
            this.setDay(d.getDay());
        }
    }

    private resetDialog(): void {
        this.initDay();
        this.subscriptionReportModel.update((model) => ({ ...model, reportTitle: "" }));
        this.resetRecipients();
    }

    private resetRecipients(): void {
        const userInfo = this.authorizationService.user() as Auth0UserProfileModel;
        const email = userInfo["https://eiendomsverdi.no/evIsUniqueUserProfile"] ? userInfo["https://eiendomsverdi.no/email"] : "";
        this.subscriptionReportModel.update((model) => ({ ...model, recipients: [{ recipient: email ? email.toLowerCase() : "" }] }));
    }
}

export interface IRecipients {
    recipient: string;
}

export interface IValidationErrors {
    noTitle: boolean;
    noRecipients: boolean;
    notValidEmails: string[];
    notValidDomains: string[];
    pendingValidation: boolean;
}

interface SubscriptionReportModel {
    reportTitle: string;
    recipients: IRecipients[];
}
